import React, { useState, useEffect, useCallback } from 'react';
import { getSettings, updateSetting, createSetting } from '../../adminApi';
import { SystemSetting } from '../../adminTypes';

interface SettingsGroup {
  [category: string]: SystemSetting[];
}

interface ToastMessage {
  type: 'success' | 'error';
  message: string;
}

interface EditingState {
  [key: string]: string | undefined;
}

const SystemSettings: React.FC = () => {
  const [settings, setSettings] = useState<SystemSetting[]>([]);
  const [settingsGroups, setSettingsGroups] = useState<SettingsGroup>({});
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<EditingState>({});
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const [expandedSections, setExpandedSections] = useState<{ [key: string]: boolean }>({});
  const [showProductionWarning, setShowProductionWarning] = useState(false);
  const [pendingEnvValue, setPendingEnvValue] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({
    setting_key: '',
    setting_value: '',
    value_type: 'string',
    category: '',
    description: '',
  });
  const [addSaving, setAddSaving] = useState(false);

  // Show toast message
  const showToast = useCallback((type: 'success' | 'error', message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 4000);
  }, []);

  // Group settings by category
  const groupSettings = (settingsData: SystemSetting[]) => {
    const groups: SettingsGroup = {};
    settingsData.forEach((setting) => {
      const category = setting.category || 'General';
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(setting);
    });
    setSettingsGroups(groups);
    // Expand first section by default
    const firstCategory = Object.keys(groups)[0];
    if (firstCategory) {
      setExpandedSections({ [firstCategory]: true });
    }
  };

  // Fetch settings on mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);
        const data = await getSettings();
        setSettings(data);
        groupSettings(data);
      } catch (error) {
        showToast(
          'error',
          error instanceof Error ? error.message : 'Failed to fetch settings'
        );
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, [showToast]);

  // Handle value edit start
  const handleEditStart = (key: string, value: string) => {
    setEditing({ ...editing, [key]: value });
  };

  // Handle value change during edit
  const handleEditChange = (key: string, newValue: string) => {
    setEditing({ ...editing, [key]: newValue });
  };

  // Handle WinTeam environment change with warning
  const handleEnvChange = (newValue: string) => {
    if (newValue === 'PRODUCTION') {
      setPendingEnvValue(newValue);
      setShowProductionWarning(true);
    } else {
      saveSettingValue('active_winteam_environment', newValue);
    }
  };

  // Confirm production environment switch
  const confirmProductionSwitch = () => {
    if (pendingEnvValue) {
      saveSettingValue('active_winteam_environment', pendingEnvValue);
    }
    setShowProductionWarning(false);
    setPendingEnvValue(null);
  };

  // Save setting value
  const saveSettingValue = async (key: string, value: string) => {
    try {
      await updateSetting(key, value);
      setSettings((prevSettings) =>
        prevSettings.map((setting) =>
          setting.setting_key === key ? { ...setting, setting_value: value } : setting
        )
      );
      groupSettings(
        settings.map((setting) =>
          setting.setting_key === key ? { ...setting, setting_value: value } : setting
        )
      );
      setEditing({ ...editing, [key]: undefined });
      showToast('success', `Setting "${key}" updated successfully`);
    } catch (error) {
      showToast(
        'error',
        error instanceof Error ? error.message : 'Failed to save setting'
      );
    }
  };

  // Handle value cell blur (save on blur)
  const handleEditBlur = (key: string) => {
    const newValue = editing[key];
    if (newValue !== undefined) {
      const originalSetting = settings.find((s) => s.setting_key === key);
      if (originalSetting && newValue !== originalSetting.setting_value) {
        saveSettingValue(key, newValue);
      } else {
        setEditing({ ...editing, [key]: undefined });
      }
    }
  };

  // Handle Enter key press in edit mode
  const handleEditKeyDown = (e: React.KeyboardEvent, key: string) => {
    if (e.key === 'Enter') {
      handleEditBlur(key);
    } else if (e.key === 'Escape') {
      setEditing({ ...editing, [key]: undefined });
    }
  };

  // Toggle section expansion
  const toggleSection = (category: string) => {
    setExpandedSections({
      ...expandedSections,
      [category]: !expandedSections[category],
    });
  };

  // Handle Add Setting form submission
  const handleAddSetting = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!addForm.setting_key.trim()) return;
    setAddSaving(true);
    try {
      const created = await createSetting({
        setting_key: addForm.setting_key.trim(),
        setting_value: addForm.setting_value,
        value_type: addForm.value_type,
        category: addForm.category.trim() || 'General',
        description: addForm.description.trim() || undefined,
      });
      const updated = [...settings, created];
      setSettings(updated);
      groupSettings(updated);
      setShowAddModal(false);
      setAddForm({ setting_key: '', setting_value: '', value_type: 'string', category: '', description: '' });
      showToast('success', `Setting "${created.setting_key}" created`);
    } catch (err: any) {
      showToast('error', err?.response?.data?.error || 'Failed to create setting');
    } finally {
      setAddSaving(false);
    }
  };

  // Find WinTeam environment setting
  const winteamEnvSetting = settings.find(
    (s) => s.setting_key === 'active_winteam_environment'
  );

  // Find Company Profile settings for quick-access card
  const COMPANY_KEYS_ORDER = [
    'company_name',
    'company_address_line1',
    'company_address_line2',
    'company_phone',
    'company_gstin',
  ];
  const companyProfileSettings = settings
    .filter((s) => COMPANY_KEYS_ORDER.includes(s.setting_key))
    .sort((a, b) => COMPANY_KEYS_ORDER.indexOf(a.setting_key) - COMPANY_KEYS_ORDER.indexOf(b.setting_key));

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.spinnerContainer}>
          <div style={styles.spinner} />
          <p>Loading settings...</p>
        </div>
      </div>
    );
  }

  const categories = Object.keys(settingsGroups).sort((a, b) => {
    // Put WinTeam first if it exists
    if (a === 'WinTeam') return -1;
    if (b === 'WinTeam') return 1;
    return a.localeCompare(b);
  });

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div>
            <h1 style={styles.title}>System Settings</h1>
            <p style={styles.subtitle}>Manage AP Invoice Portal configuration</p>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            style={styles.addButton}
          >
            + Add Setting
          </button>
        </div>
      </div>

      {/* Toast notification */}
      {toast && (
        <div
          style={{
            ...styles.toast,
            backgroundColor: toast.type === 'success' ? '#2d7a2d' : '#E33126',
          }}
        >
          {toast.message}
        </div>
      )}

      {/* WinTeam Environment Quick Access */}
      {winteamEnvSetting && (
        <div style={styles.envCard}>
          <div style={styles.envHeader}>
            <h3 style={styles.envTitle}>WinTeam Environment</h3>
            <span
              style={{
                ...styles.envBadge,
                backgroundColor:
                  winteamEnvSetting.setting_value === 'PRODUCTION'
                    ? '#E33126'
                    : '#2d7a2d',
              }}
            >
              {winteamEnvSetting.setting_value}
            </span>
          </div>
          <p style={styles.envDescription}>
            Controls both WinTeam API targeting and the active data database (PLInvoiceTraining or PLInvoiceProd).
            Changes take effect immediately for all new requests.
          </p>
          <div style={styles.envButtonGroup}>
            {['TRAINING', 'PRODUCTION'].map((env) => (
              <button
                key={env}
                onClick={() => handleEnvChange(env)}
                style={{
                  ...styles.envButton,
                  backgroundColor:
                    winteamEnvSetting.setting_value === env ? '#00386C' : '#f5f5f5',
                  color:
                    winteamEnvSetting.setting_value === env ? 'white' : '#00386C',
                  borderColor: winteamEnvSetting.setting_value === env ? '#00386C' : '#ddd',
                }}
              >
                {env}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Company Profile Quick Access */}
      {companyProfileSettings.length > 0 && (
        <div style={{ ...styles.envCard, borderColor: '#3a7abf', marginBottom: '24px' }}>
          <div style={styles.envHeader}>
            <h3 style={styles.envTitle}>Company Profile</h3>
            <span style={{ fontSize: '12px', color: '#666' }}>
              Printed on Purchase Orders &amp; documents
            </span>
          </div>
          <p style={styles.envDescription}>
            Edit any field inline — values appear in the header of generated PDFs.
            The company logo is configured under <strong>Settings → BRANDING → portal_logo_url</strong>.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            {companyProfileSettings.map((setting, idx) => {
              const isEditing = editing[setting.setting_key] !== undefined;
              // Company name spans full width
              const fullWidth = setting.setting_key === 'company_name';
              const labelMap: { [k: string]: string } = {
                company_name:          'Company Name',
                company_address_line1: 'Address Line 1',
                company_address_line2: 'Address Line 2 (City, State, Zip)',
                company_phone:         'Phone',
                company_gstin:         'Tax ID / GSTIN',
              };
              return (
                <div
                  key={setting.setting_key}
                  style={{ display: 'flex', flexDirection: 'column', gap: '4px', gridColumn: fullWidth ? '1 / -1' : undefined }}
                >
                  <label style={{ fontSize: '11px', fontWeight: '600', color: '#555', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                    {labelMap[setting.setting_key] || setting.setting_key}
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editing[setting.setting_key]}
                      onChange={(e) => handleEditChange(setting.setting_key, e.target.value)}
                      onBlur={() => handleEditBlur(setting.setting_key)}
                      onKeyDown={(e) => handleEditKeyDown(e, setting.setting_key)}
                      style={{ ...styles.editInput, margin: 0, width: '100%', boxSizing: 'border-box' as const }}
                      autoFocus
                    />
                  ) : (
                    <div
                      onClick={() => handleEditStart(setting.setting_key, setting.setting_value)}
                      style={{
                        padding: '7px 10px',
                        border: '1px solid #d0d8e4',
                        borderRadius: '4px',
                        fontSize: '13px',
                        backgroundColor: 'white',
                        cursor: 'pointer',
                        minHeight: '34px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        color: setting.setting_value ? '#333' : '#aaa',
                      }}
                    >
                      <span>{setting.setting_value || 'Click to set…'}</span>
                      <span style={{ fontSize: '10px', color: '#bbb', marginLeft: '8px', whiteSpace: 'nowrap' as const }}>edit</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Settings Sections */}
      <div style={styles.settingsSections}>
        {categories.map((category) => (
          <div key={category} style={styles.section}>
            {/* Section Header */}
            <div
              onClick={() => toggleSection(category)}
              style={styles.sectionHeader}
            >
              <div style={styles.sectionHeaderLeft}>
                <span
                  style={{
                    ...styles.expandIcon,
                    transform: expandedSections[category]
                      ? 'rotate(0deg)'
                      : 'rotate(-90deg)',
                  }}
                >
                  ▼
                </span>
                <h2 style={styles.sectionTitle}>{category}</h2>
                <span style={styles.settingCount}>
                  {settingsGroups[category].length}
                </span>
              </div>
              <button
                style={styles.expandButton}
                aria-label={
                  expandedSections[category] ? 'Collapse section' : 'Expand section'
                }
              />
            </div>

            {/* Settings Table */}
            {expandedSections[category] && (
              <div style={styles.settingsTable}>
                <div style={styles.tableHeader}>
                  <div style={{ ...styles.tableCell, flex: '1.2' }}>Key</div>
                  <div style={{ ...styles.tableCell, flex: '1.5' }}>Value</div>
                  <div style={{ ...styles.tableCell, flex: '0.8' }}>Type</div>
                  <div style={{ ...styles.tableCell, flex: '2' }}>Description</div>
                </div>

                {settingsGroups[category].map((setting) => {
                  const isEditing = editing[setting.setting_key] !== undefined;
                  const isReadOnly = !setting.editable_in_admin;

                  return (
                    <div
                      key={setting.setting_key}
                      style={{
                        ...styles.tableRow,
                        opacity: isReadOnly ? 0.6 : 1,
                        backgroundColor: isReadOnly ? '#fafafa' : 'white',
                      }}
                    >
                      <div style={{ ...styles.tableCell, flex: '1.2' }}>
                        <code style={styles.keyCode}>{setting.setting_key}</code>
                        {isReadOnly && (
                          <span style={styles.readOnlyBadge}>Read-Only</span>
                        )}
                      </div>

                      <div
                        style={{
                          ...styles.tableCell,
                          flex: '1.5',
                          padding: 0,
                        }}
                      >
                        {isReadOnly ? (
                          <div style={styles.valueCell}>
                            <span>{setting.setting_value}</span>
                          </div>
                        ) : isEditing ? (
                          <input
                            type="text"
                            value={editing[setting.setting_key]}
                            onChange={(e) =>
                              handleEditChange(setting.setting_key, e.target.value)
                            }
                            onBlur={() => handleEditBlur(setting.setting_key)}
                            onKeyDown={(e) => handleEditKeyDown(e, setting.setting_key)}
                            style={styles.editInput}
                            autoFocus
                          />
                        ) : (
                          <div
                            onClick={() =>
                              handleEditStart(setting.setting_key, setting.setting_value)
                            }
                            style={styles.valueCell}
                          >
                            <span>{setting.setting_value}</span>
                            <span style={styles.editHint}>click to edit</span>
                          </div>
                        )}
                      </div>

                      <div style={{ ...styles.tableCell, flex: '0.8' }}>
                        <span style={styles.typeBadge}>{setting.value_type}</span>
                      </div>

                      <div style={{ ...styles.tableCell, flex: '2' }}>
                        <span style={styles.description}>
                          {setting.description || '—'}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Add Setting Modal */}
      {showAddModal && (
        <div style={styles.modal}>
          <div style={{ ...styles.modalContent, maxWidth: '520px' }}>
            <h2 style={{ ...styles.modalTitle, color: '#00386C' }}>Add New Setting</h2>
            <form onSubmit={handleAddSetting}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Key *</label>
                <input
                  style={styles.formInput}
                  placeholder="e.g. my_setting_key"
                  value={addForm.setting_key}
                  onChange={e => setAddForm({ ...addForm, setting_key: e.target.value })}
                  required
                  autoFocus
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Value *</label>
                <input
                  style={styles.formInput}
                  placeholder="Setting value"
                  value={addForm.setting_value}
                  onChange={e => setAddForm({ ...addForm, setting_value: e.target.value })}
                  required
                />
              </div>
              <div style={{ display: 'flex', gap: '16px' }}>
                <div style={{ ...styles.formGroup, flex: 1 }}>
                  <label style={styles.formLabel}>Type</label>
                  <select
                    style={styles.formInput}
                    value={addForm.value_type}
                    onChange={e => setAddForm({ ...addForm, value_type: e.target.value })}
                  >
                    <option value="string">string</option>
                    <option value="integer">integer</option>
                    <option value="boolean">boolean</option>
                    <option value="json">json</option>
                  </select>
                </div>
                <div style={{ ...styles.formGroup, flex: 1 }}>
                  <label style={styles.formLabel}>Category</label>
                  <input
                    style={styles.formInput}
                    placeholder={Object.keys(settingsGroups)[0] || 'General'}
                    value={addForm.category}
                    onChange={e => setAddForm({ ...addForm, category: e.target.value })}
                    list="category-options"
                  />
                  <datalist id="category-options">
                    {Object.keys(settingsGroups).map(c => <option key={c} value={c} />)}
                  </datalist>
                </div>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Description</label>
                <input
                  style={styles.formInput}
                  placeholder="Optional — describe what this setting controls"
                  value={addForm.description}
                  onChange={e => setAddForm({ ...addForm, description: e.target.value })}
                />
              </div>
              <div style={styles.modalButtonGroup}>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setAddForm({ setting_key: '', setting_value: '', value_type: 'string', category: '', description: '' });
                  }}
                  style={styles.cancelButton}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={addSaving}
                  style={{ ...styles.confirmButton, backgroundColor: '#00386C', opacity: addSaving ? 0.7 : 1 }}
                >
                  {addSaving ? 'Saving…' : 'Add Setting'}
                </button>
              </div>
            </form>
          </div>
          <div
            style={styles.modalBackdrop}
            onClick={() => setShowAddModal(false)}
          />
        </div>
      )}

      {/* Production Warning Modal */}
      {showProductionWarning && (
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <h2 style={styles.modalTitle}>⚠️ Production Environment</h2>
            <p style={styles.modalMessage}>
              You are about to switch to the <strong>PRODUCTION</strong> environment.
              This will do two things simultaneously: invoices will be posted to the
              production WinTeam system, <strong>and</strong> the active data database
              will switch to <strong>PLInvoiceProd</strong>. All vendor, GL, job, and
              invoice data shown in the portal will reflect production records.
            </p>
            <p style={styles.modalWarning}>
              Only proceed if you are certain you want to operate against production data and WinTeam.
            </p>
            <div style={styles.modalButtonGroup}>
              <button
                onClick={() => {
                  setShowProductionWarning(false);
                  setPendingEnvValue(null);
                }}
                style={styles.cancelButton}
              >
                Cancel
              </button>
              <button
                onClick={confirmProductionSwitch}
                style={styles.confirmButton}
              >
                Confirm Switch to Production
              </button>
            </div>
          </div>
          <div
            style={styles.modalBackdrop}
            onClick={() => {
              setShowProductionWarning(false);
              setPendingEnvValue(null);
            }}
          />
        </div>
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    padding: '24px',
    maxWidth: '1200px',
    margin: '0 auto',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    color: '#333',
  },
  header: {
    marginBottom: '32px',
    borderBottom: '1px solid #ddd',
    paddingBottom: '16px',
  },
  title: {
    fontSize: '28px',
    fontWeight: '600',
    color: '#00386C',
    margin: '0 0 8px 0',
  },
  subtitle: {
    fontSize: '14px',
    color: '#666',
    margin: '0',
  },
  spinnerContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px',
    gap: '16px',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '3px solid #f3f3f3',
    borderTop: '3px solid #00386C',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  toast: {
    padding: '12px 16px',
    borderRadius: '4px',
    marginBottom: '20px',
    color: 'white',
    fontSize: '14px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  envCard: {
    backgroundColor: '#f5f5f5',
    border: '2px solid #00386C',
    borderRadius: '8px',
    padding: '20px',
    marginBottom: '32px',
  },
  envHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '12px',
  },
  envTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#00386C',
    margin: '0',
  },
  envBadge: {
    padding: '4px 12px',
    borderRadius: '20px',
    color: 'white',
    fontSize: '12px',
    fontWeight: '600',
  },
  envDescription: {
    fontSize: '13px',
    color: '#555',
    margin: '0 0 16px 0',
  },
  envButtonGroup: {
    display: 'flex',
    gap: '12px',
  },
  envButton: {
    padding: '8px 16px',
    borderRadius: '4px',
    border: '1px solid #ddd',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  settingsSections: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  section: {
    backgroundColor: 'white',
    border: '1px solid #ddd',
    borderRadius: '6px',
    overflow: 'hidden',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px',
    backgroundColor: '#f5f5f5',
    cursor: 'pointer',
    borderBottom: '1px solid #ddd',
    transition: 'background-color 0.2s',
  },
  sectionHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  expandIcon: {
    display: 'inline-block',
    transition: 'transform 0.3s',
    color: '#00386C',
    fontSize: '12px',
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#00386C',
    margin: '0',
  },
  settingCount: {
    backgroundColor: '#00386C',
    color: 'white',
    padding: '2px 8px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600',
  },
  expandButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '0',
  },
  settingsTable: {
    display: 'flex',
    flexDirection: 'column',
  },
  tableHeader: {
    display: 'flex',
    padding: '12px 16px',
    backgroundColor: '#f9f9f9',
    borderBottom: '1px solid #ddd',
    fontWeight: '600',
    fontSize: '12px',
    color: '#666',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  tableRow: {
    display: 'flex',
    padding: '0',
    borderBottom: '1px solid #eee',
    transition: 'background-color 0.2s',
  },
  tableCell: {
    padding: '12px 16px',
    display: 'flex',
    alignItems: 'center',
    fontSize: '13px',
  },
  keyCode: {
    backgroundColor: '#f5f5f5',
    padding: '2px 6px',
    borderRadius: '3px',
    fontFamily: 'Courier, monospace',
    fontSize: '12px',
    color: '#00386C',
    marginRight: '8px',
  },
  readOnlyBadge: {
    backgroundColor: '#ddd',
    color: '#666',
    padding: '2px 6px',
    borderRadius: '3px',
    fontSize: '11px',
    fontWeight: '600',
    marginLeft: '4px',
  },
  valueCell: {
    width: '100%',
    padding: '12px 16px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    minHeight: '40px',
    transition: 'background-color 0.2s',
  },
  editInput: {
    width: '100%',
    padding: '8px 12px',
    border: '2px solid #00386C',
    borderRadius: '4px',
    fontSize: '13px',
    fontFamily: 'inherit',
    margin: '12px 16px',
  },
  editHint: {
    fontSize: '11px',
    color: '#999',
    whiteSpace: 'nowrap',
    marginLeft: 'auto',
  },
  typeBadge: {
    backgroundColor: '#e8f4f8',
    color: '#00386C',
    padding: '2px 8px',
    borderRadius: '3px',
    fontSize: '11px',
    fontWeight: '600',
  },
  description: {
    color: '#666',
    lineHeight: '1.4',
  },
  modal: {
    position: 'fixed',
    top: '0',
    left: '0',
    right: '0',
    bottom: '0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: '1000',
  },
  modalBackdrop: {
    position: 'absolute',
    top: '0',
    left: '0',
    right: '0',
    bottom: '0',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: '-1',
  },
  modalContent: {
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '32px',
    maxWidth: '500px',
    boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
    zIndex: '1001',
  },
  modalTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#E33126',
    margin: '0 0 16px 0',
  },
  modalMessage: {
    fontSize: '14px',
    color: '#333',
    margin: '0 0 12px 0',
    lineHeight: '1.6',
  },
  modalWarning: {
    fontSize: '13px',
    color: '#E33126',
    fontWeight: '500',
    margin: '0 0 24px 0',
  },
  modalButtonGroup: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
  },
  cancelButton: {
    padding: '8px 16px',
    backgroundColor: '#f5f5f5',
    color: '#333',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  confirmButton: {
    padding: '8px 16px',
    backgroundColor: '#E33126',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  addButton: {
    padding: '8px 18px',
    backgroundColor: '#00386C',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    whiteSpace: 'nowrap' as const,
  },
  formGroup: {
    marginBottom: '16px',
  },
  formLabel: {
    display: 'block',
    fontSize: '13px',
    fontWeight: '600',
    color: '#333',
    marginBottom: '6px',
  },
  formInput: {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '13px',
    fontFamily: 'inherit',
    boxSizing: 'border-box' as const,
  },
};

export default SystemSettings;
